'use client';

import React from 'react';
import type { ReactNode } from 'react';
import { cx, sortCx } from '../../../utils/cx';
import { LoadingSpinner, type LoadingSpinnerProps } from './LoadingSpinner';

export type LoadingIndicatorProps = {
    /** The spinner size, with matching text and gap scale. */
    size?: LoadingSpinnerProps['size'];
    /** Loading message shown beside the spinner. */
    children?: ReactNode;
    /** Additional classes for the indicator wrapper. */
    className?: string;
    /** Additional classes for the spinner. */
    spinnerClassName?: string;
};

const styles = sortCx({
    size: {
        xxs: 'gap-sm text-sm',
        xs: 'gap-md text-sm',
        sm: 'gap-md text-md',
        md: 'gap-lg text-lg',
        lg: 'gap-xl text-lg',
        xl: 'gap-xl text-display-md',
        '2xl': 'gap-2xl text-display-md',
        '3xl': 'gap-2xl text-display-md',
    },
});

/** Renders a loading spinner with supporting loading text. */
export const LoadingIndicator: React.FC<LoadingIndicatorProps> & { Spinner: typeof LoadingSpinner } = Object.assign(
    ({ size = 'sm', children = 'Loading...', className, spinnerClassName }: LoadingIndicatorProps) => {
        return (
            <div
                className={cx(
                    'flex flex-row items-center justify-center whitespace-nowrap text-secondary font-medium',
                    styles.size[size],
                    className,
                )}
            >
                <LoadingSpinner size={size} className={spinnerClassName} />
                {children}
            </div>
        );
    },
    { Spinner: LoadingSpinner },
);

LoadingIndicator.displayName = 'LoadingIndicator';
